// src/pages/Accounting/Reconcile/Session/MarkReconciledModal.tsx

import type React from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { PALETTE } from "../constants";
import type { Account, Currency } from "../types";
import { formatAccountLabel, formatCurrency, formatDateLong } from "../utils";

interface MarkReconciledModalProps {
  open: boolean;
  account: Account | undefined;
  periodStart: string;
  periodEnd: string;
  statementEndingBalance: number;
  novalaCleared: number;
  paymentsTotal: number;
  paymentsCount: number;
  depositsTotal: number;
  depositsCount: number;
  currency: Currency;
  signerName: string;
  onCancel: () => void;
  onConfirm: (payload: { confirmed: boolean; attachment: File | null }) => void;
}

export function MarkReconciledModal({
  open,
  account,
  periodStart,
  periodEnd,
  statementEndingBalance,
  novalaCleared,
  paymentsTotal,
  paymentsCount,
  depositsTotal,
  depositsCount,
  currency,
  signerName,
  onCancel,
  onConfirm,
}: MarkReconciledModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset local state whenever the modal opens afresh
  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setAttachment(null);
      setSubmitting(false);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const accountLabel = account ? formatAccountLabel(account) : "Unknown account";
  const signedOnLabel = formatDateLong(new Date().toISOString().slice(0, 10));
  const periodLabel = `${formatDateLong(periodStart)} to ${formatDateLong(periodEnd)}`;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setAttachment(f);
  };

  const handleConfirm = () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    onConfirm({ confirmed, attachment });
  };

  return createPortal(
    <div style={overlayStyle} onMouseDown={onCancel}>
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Reconciliation balanced</div>
            <h3 style={titleStyle}>Sign and lock this period</h3>
            <div style={subtitleStyle}>
              Once locked, this period moves to History. You can undo later from the History by account page.
            </div>
          </div>
          <button type="button" onClick={onCancel} style={closeBtnStyle} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Summary */}
        <div style={summaryStyle}>
          <div style={sectionLabelStyle}>Summary</div>
          <div style={summaryGridStyle}>
            <SummaryRow label="Account" value={accountLabel} />
            <SummaryRow label="Statement ending" value={formatDateLong(periodEnd)} />
            <SummaryRow label="Period" value={periodLabel} />
            <SummaryRow label="Statement balance" value={formatCurrency(statementEndingBalance, currency)} mono />
            <SummaryRow label="Cleared balance" value={formatCurrency(novalaCleared, currency)} mono />
            <SummaryRow label="Payments cleared" value={`${paymentsCount} \u00b7 ${formatCurrency(Math.abs(paymentsTotal), currency)}`} mono />
            <SummaryRow label="Deposits cleared" value={`${depositsCount} \u00b7 ${formatCurrency(depositsTotal, currency)}`} mono />
          </div>
          <div style={diffRowStyle}>
            <span style={{ fontSize: 13, color: PALETTE.ink2, fontWeight: 500 }}>Difference</span>
            <span style={diffValueStyle}>
              <span style={diffCheckStyle}><Check size={11} strokeWidth={3} color="#fff" /></span>
              {formatCurrency(0, currency)}
            </span>
          </div>
        </div>

        {/* Attachment */}
        <div style={blockStyle}>
          <div style={fieldLabelStyle}>Attach statement (optional)</div>
          <label style={dropZoneStyle}>
            <input type="file" accept="application/pdf" onChange={handleFile} style={{ display: "none" }} />
            <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 500 }}>
              {attachment ? attachment.name : (
                <>
                  Drop a PDF here, or <span style={{ color: PALETTE.tealDark, fontWeight: 600 }}>browse</span>
                </>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: PALETTE.ink4, marginTop: 2 }}>
              PDF only, max 10 MB. Helps auditors trace the balance.
            </div>
          </label>
        </div>

        {/* Sign-off */}
        <div style={blockStyle}>
          <div style={sectionLabelStyle}>Sign-off</div>
          <div style={signOffGridStyle}>
            <div>
              <div style={fieldLabelStyle}>Signed by</div>
              <div style={lockedFieldStyle}>{signerName}</div>
            </div>
            <div>
              <div style={fieldLabelStyle}>Signed on</div>
              <div style={{ ...lockedFieldStyle, fontVariantNumeric: "tabular-nums" }}>{signedOnLabel}</div>
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ display: "none" }}
            />
            <span style={{ ...checkboxStyle, ...(confirmed ? checkboxCheckedStyle : {}) }}>
              {confirmed && <Check size={11} strokeWidth={3} color="#fff" />}
            </span>
            <span style={{ fontSize: 13, color: PALETTE.ink2, fontWeight: 500, lineHeight: 1.5 }}>
              I confirm this period is reconciled and understand it will be locked. Transactions in this period cannot be edited after locking without an undo.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={onCancel} style={cancelBtnStyle} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!confirmed || submitting}
            style={{
              ...confirmBtnStyle,
              background: confirmed && !submitting ? "#0E1A1A" : PALETTE.ink4,
              cursor: confirmed && !submitting ? "pointer" : "not-allowed",
            }}
          >
            {submitting ? "Locking..." : "Sign and lock"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: PALETTE.ink3 }}>{label}</span>
      <strong style={{ color: PALETTE.ink, fontVariantNumeric: mono ? "tabular-nums" : "normal" }}>{value}</strong>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.5)",
  backdropFilter: "blur(2px)",
  zIndex: 2000,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: 72,
  overflowY: "auto",
};
const modalStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 14,
  width: 560,
  maxWidth: "calc(100% - 32px)",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
  overflow: "hidden",
  marginBottom: 72,
};
const headerStyle: React.CSSProperties = {
  padding: "20px 24px 16px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
};
const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: PALETTE.tealDark,
  fontWeight: 700,
  marginBottom: 6,
};
const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  margin: 0,
  letterSpacing: "-0.01em",
  color: PALETTE.ink,
};
const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: PALETTE.ink3,
  marginTop: 4,
};
const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink4,
  cursor: "pointer",
  padding: 4,
  lineHeight: 1,
};
const summaryStyle: React.CSSProperties = {
  padding: "20px 24px",
  background: PALETTE.bgFade,
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};
const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: PALETTE.ink3,
  fontWeight: 700,
  marginBottom: 12,
};
const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "14px 24px",
  fontSize: 13.5,
};
const diffRowStyle: React.CSSProperties = {
  marginTop: 14,
  paddingTop: 12,
  borderTop: `1px dashed ${PALETTE.line}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const diffValueStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: PALETTE.tealDark,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontVariantNumeric: "tabular-nums",
};
const diffCheckStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "50%",
  background: PALETTE.tealDark,
};
const blockStyle: React.CSSProperties = {
  padding: "20px 24px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: PALETTE.ink2,
  fontWeight: 600,
  marginBottom: 7,
  display: "block",
};
const dropZoneStyle: React.CSSProperties = {
  display: "block",
  border: `1px dashed ${PALETTE.line}`,
  borderRadius: 8,
  padding: "14px 16px",
  background: "#fff",
  cursor: "pointer",
};
const signOffGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 160px",
  gap: 12,
};
const lockedFieldStyle: React.CSSProperties = {
  padding: "10px 14px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  background: "#fff",
  fontSize: 14,
  color: PALETTE.ink,
  fontWeight: 500,
};
const checkboxStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  border: `1.5px solid ${PALETTE.ink4}`,
  borderRadius: "50%",
  background: "#fff",
  flexShrink: 0,
  marginTop: 1,
};
const checkboxCheckedStyle: React.CSSProperties = {
  border: `1.5px solid ${PALETTE.tealDark}`,
  background: PALETTE.tealDark,
};
const footerStyle: React.CSSProperties = {
  padding: "16px 24px",
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
};
const cancelBtnStyle: React.CSSProperties = {
  background: "#fff",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  fontWeight: 500,
  fontSize: 13.5,
  padding: "10px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontFamily: "inherit",
};
const confirmBtnStyle: React.CSSProperties = {
  color: "#fff",
  border: 0,
  fontWeight: 600,
  fontSize: 13.5,
  padding: "10px 24px",
  borderRadius: 10,
  fontFamily: "inherit",
};